import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  Stack,
  Divider,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  PushPin as PinIcon,
  PushPinOutlined as PinOutlinedIcon,
  Archive as ArchiveIcon,
  TaskAlt as TaskIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { NoteEditor } from './NoteEditor';
import { useClients } from '../../hooks/useClients';
import type { Client } from '../../services/clients';
import type {
  NoteWithRelations,
  CreateNoteRequest,
  UpdateNoteRequest,
} from '../../types/note';

interface NoteDetailModalProps {
  open: boolean;
  onClose: () => void;
  note?: NoteWithRelations | null;
  onSave: (data: CreateNoteRequest | UpdateNoteRequest) => Promise<void>;
  onPin?: (note: NoteWithRelations) => void;
  onArchive?: (note: NoteWithRelations) => void;
  onConvertToTask?: (note: NoteWithRelations) => void;
  onDelete?: (note: NoteWithRelations) => void;
  isLoading?: boolean;
}

export const NoteDetailModal: React.FC<NoteDetailModalProps> = ({
  open,
  onClose,
  note,
  onSave,
  onPin,
  onArchive,
  onConvertToTask,
  onDelete,
  isLoading = false,
}) => {
  const isEditing = !!note;
  const [formData, setFormData] = useState<CreateNoteRequest | UpdateNoteRequest>({});

  const { data: clientsData } = useClients();
  const clients: Client[] = clientsData?.clients || [];

  // Reset form when note changes
  useEffect(() => {
    setFormData({});
  }, [note, open]);

  const handleFormChange = (data: CreateNoteRequest | UpdateNoteRequest) => {
    setFormData(data);
  };

  const handleSubmit = async () => {
    if (!formData.title?.trim()) return;

    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving note:', error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {isEditing ? 'Edit Note' : 'New Note'}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            {isEditing && note && (
              <>
                {onPin && (
                  <IconButton onClick={() => onPin(note)} size="small">
                    {note.isPinned ? <PinIcon color="primary" /> : <PinOutlinedIcon />}
                  </IconButton>
                )}
                {onArchive && (
                  <IconButton onClick={() => onArchive(note)} size="small">
                    <ArchiveIcon color={note.isArchived ? 'primary' : 'inherit'} />
                  </IconButton>
                )}
                {onConvertToTask && !note.convertedToTaskId && (
                  <IconButton onClick={() => onConvertToTask(note)} size="small">
                    <TaskIcon />
                  </IconButton>
                )}
                {onDelete && (
                  <IconButton onClick={() => onDelete(note)} size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                )}
              </>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {isEditing && note && (
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
              <Typography variant="caption" color="text.secondary">
                Created {formatDistanceToNow(new Date(note.createdAt), { addSuffix: true })}
              </Typography>
              {note.isPinned && <Chip label="Pinned" size="small" color="primary" variant="outlined" />}
              {note.isArchived && <Chip label="Archived" size="small" variant="outlined" />}
              {note.convertedToTaskId && (
                <Chip icon={<TaskIcon />} label="Converted to task" size="small" color="success" variant="outlined" />
              )}
            </Stack>
            <Divider sx={{ mt: 2 }} />
          </Box>
        )}

        <NoteEditor
          note={note}
          onChange={handleFormChange}
          clients={clients}
          autoFocus={!isEditing}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!formData.title?.trim() || isLoading}
        >
          {isLoading ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Note'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NoteDetailModal;
